import React, { useEffect, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { Link, useLocation } from "react-router-dom";
import { BreadCrumb } from "primereact/breadcrumb";
import "primereact/resources/primereact.min.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import { FaHome, FaChevronUp, FaChevronDown, FaMapMarkedAlt, FaImages, FaBlog, FaCalendarAlt, FaClipboardList, FaInfoCircle } from "react-icons/fa";
import Header, { NavLink, LogoLink, NavToggle, DesktopNavLinks } from "./light.js";

const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV;

const StyledHeader = styled(Header)`
  ${tw`pt-4 max-w-none w-full`}
  ${DesktopNavLinks} ${NavLink}, ${LogoLink} {
    ${tw`text-white hover:border-white`}
    font-family: 'Roboto', sans-serif !important;
  }
  ${NavToggle}.closed {
    ${tw`text-white hover:text-[#0049AC]`}
  }
  ${LogoLink} {
    ${tw`text-xl md:text-3xl flex items-center`};
  }
  ${({ isSticky }) => isSticky && css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    background-color: #0049AC;
    transition: top 0.3s ease, background-color 0.3s ease;
    padding: 8px;
  `}
  ${LogoLink} img {
    ${({ isSticky }) => isSticky && tw`hidden`}
  }
  ${LogoLink} span {
    display: none;
    ${({ isSticky }) => isSticky && tw`block text-white font-bold`}
  }
`;

const StyledNavLink = styled(NavLink)`
  ${tw`relative flex flex-col items-center text-white font-bold antialiased py-3 px-4`};
  font-size: 14px;
  font-family: 'Roboto', sans-serif !important;
  transition: all 0.3s ease-in-out;
  border-bottom: 2px solid transparent;
  &:hover, &.hover-active {
    transform: scale(1.1);
    border-bottom: 2px solid white;
  }
`;

const IconWrapper = styled.div`
  ${tw`mb-1 text-lg w-6 h-6`};
`;

const NavContainer = styled.div`
  ${tw`flex flex-col md:flex-row items-center justify-center w-full`}
`;

const Container = styled.div`
  ${tw`relative -mx-8 -mt-8 bg-center bg-cover h-screen min-h-48`}
  background-image: ${({ bgImage }) => `url(${bgImage})`};
  height: 230px;
`;

const HeroContainer = tw.div`z-20 relative px-2 sm:px-4 mx-auto h-full flex flex-col`;

const ModalContent = styled.div`
  ${tw`relative flex flex-col justify-start bg-white shadow-lg p-6 rounded-lg mr-20 ml-20 mb-20 h-3/4 w-3/4 z-20 pointer-events-auto`}
  top: 78px;
  overflow: hidden;
  font-family: 'Roboto', sans-serif !important;
  & > div {
    display: flex;
    white-space: nowrap;
    gap: 32px;
  }
`;

const Modal = tw.div`
  fixed z-20 inset-0 flex items-center justify-center 
  transition-transform duration-300 ease-out
  animate-[fadeIn_0.3s_ease-out_forwards]
  pointer-events-none
`;

const DropdownToggle = styled.button`
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  margin-left: 10px;
`;

const DropdownContent = styled.div`
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
  background: #0049AC;
  padding: 10px;
  width: 100%;
  height: auto;
  overflow: hidden;
  font-family: 'Roboto', sans-serif !important;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const SubmenuTitle = styled.div`
  font-weight: bold;
  margin-top: 10px;
`;

const fallbackMenuData = {
  destinations: {
    title: "Explore Destinations",
    submenus: [
      {
        title: "Domestic Destinations",
        menus: [
          { name: "India", link: "destinations/explore/india" },
          { name: "Thailand", link: "destinations/explore/thailand" },
          { name: "Japan", link: "destinations/explore/japan" },
        ]
      },
      {
        title: "International Destinations",
        menus: [
          { name: "Italy", link: "destinations/explore/italy" },
          { name: "France", link: "destinations/explore/france" },
          { name: "Germany", link: "destinations/explore/germany" },
        ]
      },
    ]
  },
  checklists: {
    title: "Visa Checklist",
    submenus: [
      {
        title: "Europe Visa Checklist",
        menus: [
          { name: "USA", link: "/visa/tourist/usa" },
          { name: "UK", link: "/visa/tourist/uk" },
        ]
      },
      {
        title: "Asia Visa Checklist",
        menus: [
          { name: "Canada", link: "/visa/business/canada" },
          { name: "Australia", link: "/visa/business/australia" },
        ]
      }
    ]
  }
};

// Helper function to sort menus alphabetically
const sortMenus = (data) => {
  const sortedData = { ...data };
  ["destinations", "checklists"].forEach((key) => {
    if (sortedData[key]?.submenus) {
      sortedData[key].submenus = sortedData[key].submenus.map((submenu) => ({
        ...submenu,
        menus: [...submenu.menus].sort((a, b) => a.name.localeCompare(b.name)),
      }));
    }
  });
  return sortedData;
};

export default ({ pageName, titleName, headStick }) => {
  const location = useLocation();
  const [isSticky, setIsSticky] = useState(false);
  const [bgImage, setBgImage] = useState("https://images.pexels.com/photos/2166553/pexels-photo-2166553.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2");
  const [submenuData, setSubmenuData] = useState(sortMenus(fallbackMenuData)); // Sort fallback data initially
  const [activeMenu, setActiveMenu] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [openMenu, setOpenMenu] = useState(null);

  const toggleDropdown = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  const hasSubmenu = (menu) => ["destinations", "checklists"].includes(menu);

  useEffect(() => {
    setModalVisible(false);
    setOpenMenu(null);
    setActiveMenu(null);
    document.body.style.overflow = 'auto';
  }, [location.pathname]);

  const handleLinkClick = (toggleNavbar) => {
    setModalVisible(false);
    setOpenMenu(null);
    setActiveMenu(null);
    document.body.style.overflow = 'auto';
    if (isMobile && toggleNavbar) {
      toggleNavbar(); // Close the mobile navigation toggle
    }
  };

  const navLinks = [
    <NavContainer key={1}>
      <StyledNavLink
        as={Link}
        to="/pg/destinations"
        onMouseEnter={() => handleMouseEnter("destinations")}
        onMouseLeave={(event) => handleMouseGoneMenu("destinations", event)}
        className={`${activeMenu === "destinations" && !isMobile ? "hover-active" : ""}`}
      >
        <IconWrapper><FaMapMarkedAlt /></IconWrapper>
        EXPLORE DESTINATIONS
      </StyledNavLink>
      {isMobile && (
        <DropdownToggle onClick={() => toggleDropdown("destinations")}>
          <span style={{ marginLeft: "-12px", display: "flex", alignItems: "center" }}>
            {openMenu === "destinations" ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
          </span>
          <br />
        </DropdownToggle>
      )}
      {isMobile && openMenu === "destinations" && (
        <DropdownContent isOpen={openMenu === "destinations"}>
          {submenuData.destinations.submenus.map((submenu, index) => (
            <div key={index}>
              <SubmenuTitle>{submenu.title}</SubmenuTitle>
              {submenu.menus.map((item, i) => (
                <Link
                  key={i}
                  to={`/pg/${item.link || ''}`}
                  style={{ display: "block", margin: "5px 0" }}
                  onClick={(e) => {
                    handleLinkClick(e.target.closest('nav')?.toggleNavbar);
                    if (window.innerWidth <= 768) {
                      e.preventDefault();
                      window.location.href = `/pg/${item.link || ''}`;
                    }
                  }}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          ))}
        </DropdownContent>
      )}
      <StyledNavLink as={Link} to="/pg/gallery">
        <IconWrapper><FaImages /></IconWrapper>
        GALLERY
      </StyledNavLink>
      <StyledNavLink as={Link} to="/pg/blogs">
        <IconWrapper><FaBlog /></IconWrapper>
        BLOGS
      </StyledNavLink>
      <StyledNavLink as={Link} to="/pg/events">
        <IconWrapper><FaCalendarAlt /></IconWrapper>
        EVENTS & ACTIVITIES
      </StyledNavLink>
      <StyledNavLink
        as={Link}
        to="/pg/visa-checklist"
        onMouseEnter={() => handleMouseEnter("checklists")}
        onMouseLeave={(event) => handleMouseGoneMenu("checklists", event)}
        className={`${activeMenu === "checklists" && !isMobile ? "hover-active" : ""}`}
      >
        <IconWrapper><FaClipboardList /></IconWrapper>
        VISA CHECKLIST
      </StyledNavLink>
      {isMobile && (
        <DropdownToggle onClick={() => toggleDropdown("checklists")}>
          <span style={{ marginLeft: "-12px", display: "flex", alignItems: "center" }}>
            {openMenu === "checklists" ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
          </span>
          <br />
        </DropdownToggle>
      )}
      {isMobile && openMenu === "checklists" && (
        <DropdownContent isOpen={openMenu === "checklists"}>
          {submenuData.checklists.submenus.map((submenu, index) => (
            <div key={index}>
              <SubmenuTitle>{submenu.title}</SubmenuTitle>
              {submenu.menus.map((item, i) => (
                <Link
                  key={i}
                  to={item.link}
                  style={{ display: "block", margin: "5px 0" }}
                  onClick={(e) => handleLinkClick(e.target.closest('nav')?.toggleNavbar)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          ))}
        </DropdownContent>
      )}
      <StyledNavLink as={Link} to="/pg/know-us">
        <IconWrapper><FaInfoCircle /></IconWrapper>
        KNOW US
      </StyledNavLink>
    </NavContainer>
  ];

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      const path = location.pathname.replace(/^\/pg\//, "").toLowerCase().replace(/ & /g, "-").replace(/ /g, "-");
      try {
        const response = await fetch(`${API_URL}/web/home/page-header?title=${path}`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY
          }
        });
        const result = await response.json();
        if (result.header.resCode === 200 && result.data.length > 0) {
          setBgImage(result.data[0].content);
        }
      } catch (error) {
        console.error("Failed to fetch background image:", error);
      }
    };

    const fetchSubmenuData = async () => {
      try {
        const response = await fetch(`${API_URL}/web/home/menu`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY
          }
        });
        const result = await response.json();
        if (result.header.resCode === 200 && result.data.length > 0) {
          setSubmenuData(sortMenus(result.data[0])); // Sort fetched submenu data
        }
      } catch (error) {
        console.error("Error fetching submenu data:", error);
        setSubmenuData(sortMenus(fallbackMenuData)); // Sort fallback data on error
      }
    };

    fetchSubmenuData();
    fetchBackgroundImage();
  }, [location.pathname]);

  const handleScroll = () => {
    if (window.pageYOffset > 100) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  };

  const handleMouseEnter = (menu) => {
    if (hasSubmenu(menu) && !isMobile && !modalVisible) {
      setActiveMenu(menu);
      setModalVisible(true);
      document.body.style.overflow = 'hidden';
    }
  };

  const handleMouseGoneMenu = (menu, event) => {
    if (event.relatedTarget && event.clientY < event.currentTarget.getBoundingClientRect().bottom) {
      setActiveMenu(null);
      setModalVisible(false);
      document.body.style.overflow = 'auto';
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setModalVisible(false);
      setActiveMenu(null);
      document.body.style.overflow = 'auto';
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const items = [
    { label: "Home", url: "/" },
    { label: pageName || "Traviator Holidays" }
  ];

  if (titleName) {
    items.push({ label: titleName });
  }

  const home = { icon: <FaHome />, url: "/" };

  return (
    <>
      <Container bgImage={bgImage}>
        <HeroContainer className="hero-container">
          <div className="hero-container relative z-20 px-2 sm:px-4 mx-auto h-full flex flex-col">
            <StyledHeader links={navLinks} isSticky={headStick == null ? isSticky : headStick} />
          </div>
          { !isMobile && <br />}
          <div className="w-full overflow-x-auto overflow-y-hidden whitespace-nowrap h-10 flex items-center px-4 bg-white shadow-md rounded-md">
            <BreadCrumb
              model={items}
              home={home}
              className="text-gray-800 flex-nowrap inline-flex items-center h-full"
            />
          </div>
          <br />
        </HeroContainer>
      </Container>
      {modalVisible && !isMobile && (
        <Modal>
          <ModalContent
            onMouseLeave={handleMouseLeave}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", gap: "8px", padding: "4px" }}>
              {submenuData[activeMenu]?.submenus?.map((item, index) => (
                <div key={index} style={{ width: "300px", padding: "8px", position: "relative", borderRadius: "2px" }}>
                  <h3 style={{ fontSize: "18px", fontWeight: "900", marginBottom: "4px", position: "sticky", top: "0", padding: "4px", zIndex: "10" }}>
                    {item.title}
                  </h3>
                  <div style={{ height: "400px" }}>
                    <ul style={{ listStyleType: "none", padding: "0", margin: "0" }}>
                      {item.menus?.map((subItem, subIndex) => (
                        <li
                          key={subIndex}
                          style={{
                            padding: "1px",
                            marginLeft: "4px",
                            backgroundColor: "white",
                            transition: "transform 0.2s, color 0.2s, background-color 0.2s",
                            cursor: "pointer"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "#0049AC";
                            e.currentTarget.style.transform = "scale(1.05)";
                            e.currentTarget.style.backgroundColor = "#f0f8ff";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "#000";
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.backgroundColor = "white";
                          }}
                        >
                          <Link
                            to={activeMenu === "checklists" ? subItem.link : `/pg/${subItem.link || ''}`}
                            target={activeMenu === "checklists" ? "_blank" : "_self"}
                            style={{ textDecoration: "none", color: "inherit", display: "block", width: "100%", height: "100%", padding: "4px" }}
                            onClick={(e) => handleLinkClick(e.target.closest('nav')?.toggleNavbar)}
                          >
                            {subItem.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};